import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import {
  Microsoft360View,
  Microsoft365License,
  Microsoft365LicenseCount,
  Microsoft365LicensesFieldConfig,
  Microsoft365UserLicenses,
  MicrosoftSecurityGroupOption,
} from '../../../shared/models/generated/ziroModels';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { Microsoft365UserLicensesPayload } from './microsoft365-info-licenses/microsoft365-info-licenses.component';
import { Microsoft360ViewContext } from '../../../shared/contexts/microsoft-360-view.context';
import { combineLatest, Subscription, switchMap } from 'rxjs';
import { MicrosoftLicensesContext } from '../../../shared/contexts/microsoft-licenses.context';
import { UsageLocationContext } from '../../../shared/contexts/usage-location.context';
import { Microsoft360ViewPollingService } from '../../../shared/services/microsoft/microsoft-360-view-polling.service';
import { MicrosoftTenantsStateContext } from '../../../shared/contexts/microsoft-tenants-state.context';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'ziro-microsoft-view-licenses',
  templateUrl: './microsoft-view-licenses.component.html',
  standalone: false,
  providers: [UsageLocationContext, Microsoft360ViewPollingService],
})
export class MicrosoftViewLicensesComponent implements OnInit, OnDestroy {
  private microsoft360ViewContext = inject(Microsoft360ViewContext);
  private microsoftLicensesContext = inject(MicrosoftLicensesContext);
  private _tenantsStateContext = inject(MicrosoftTenantsStateContext);

  isLoading = true;
  pageDisabled = true;
  username: string;
  tenantDefaultUsageLocation: string;
  licenseConfig: Microsoft365LicensesFieldConfig;
  userUsageLocation: string;
  ziroIcons = ZiroIcons;
  inventory: Microsoft365LicenseCount[] = [];
  userLicenses: Microsoft365UserLicenses;
  isReady = false;
  initialData: Microsoft365UserLicensesPayload = { licenses: {}, usageLocation: '' };
  userSecurityGroups: MicrosoftSecurityGroupOption[];
  microsoft360View: Microsoft360View;
  tenantId: string;

  private _subscriptions = new Subscription();

  ngOnInit(): void {
    const tenantStateSub = this._tenantsStateContext.cachedTenantState$
      .pipe(
        filter((microsoftTenantFieldConfig) => !!microsoftTenantFieldConfig?.tenantId),
        switchMap((microsoftTenantFieldConfig) => {
          this.tenantId = microsoftTenantFieldConfig.tenantId.toString();
          return this.microsoftLicensesContext.refresh();
        }),
      )
      .subscribe(() => {
        this._getInitialData();
      });

    this._subscriptions.add(tenantStateSub);
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  private _getInitialData(): void {
    const sub = combineLatest([this.microsoft360ViewContext.state$, this.microsoftLicensesContext.state$]).subscribe(
      ([microsoft360View, licensesConfig]) => {
        this.microsoft360View = microsoft360View;
        this.userLicenses = microsoft360View.microsoft365UserLicenses;
        this.userSecurityGroups = microsoft360View.securityGroupMemberships;
        this.inventory = licensesConfig.writeLicenseList.concat(licensesConfig.readOnlyLicenseList);
        this.tenantDefaultUsageLocation = licensesConfig.usageLocation.defaultValue;
        this.licenseConfig = licensesConfig;
        this.userUsageLocation = microsoft360View.microsoft365UserLicenses.usageLocation;
        this._setInitialData();
        this.isLoading = false;
        this.isReady = true;
        this.pageDisabled = false;
      },
    );

    this._subscriptions.add(sub);
  }

  private _setInitialData(): void {
    this.initialData.usageLocation =
      this.userUsageLocation !== '' ? this.userUsageLocation : this.tenantDefaultUsageLocation;
    this.inventory.forEach((license: Microsoft365LicenseCount) => {
      const skuId = license.microsoft365LicenseJson.skuId;
      const name = license.microsoft365LicenseJson.productName || license.microsoft365LicenseJson.skuPartNumber;
      this.initialData.licenses[name] = this.userLicenses.licenses.some((userLicense: Microsoft365License) => {
        return userLicense.skuId === skuId;
      });
    });
  }
}
